import { RichText } from '@brunommsilva/react-foundations';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import AssistantAvatar from './assistant-avatar';

interface ConversationBarProps {
  /** The conversation title, once there is a conversation. */
  title: string | null;
  messageCount: number;
  onBack: () => void;
  onClear: () => void;
}

/** The header of the assistant page: the way back, the conversation title and length, and a way to start over. */
export default function ConversationBar({
  title,
  messageCount,
  onBack,
  onClear,
}: ConversationBarProps) {
  const { t } = useTranslation();
  const hasMessages = messageCount > 0;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        pl: 0.5,
        pr: 1,
        py: 1,
        bgcolor: 'action.hover',
        boxShadow: '0 1px 4px rgba(0, 0, 0, 0.25)',
        zIndex: 1,
      }}
    >
      <IconButton aria-label={t('ASSISTANT_BACK')} onClick={onBack}>
        <ArrowBackIcon />
      </IconButton>
      <AssistantAvatar size={32} iconSize={18} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          noWrap
          sx={{ fontWeight: 600, color: 'text.primary' }}
        >
          {title ? <RichText text={title} emoji /> : t('ASSISTANT_TITLE')}
        </Typography>
        {hasMessages && (
          <Typography
            variant="caption"
            noWrap
            sx={{ display: 'block', color: 'text.secondary', lineHeight: 1.2 }}
          >
            {t('ASSISTANT_MESSAGE_COUNT', { count: messageCount })}
          </Typography>
        )}
      </Box>
      {hasMessages && (
        <Tooltip title={t('ASSISTANT_CLEAR')}>
          <IconButton
            size="small"
            aria-label={t('ASSISTANT_CLEAR')}
            onClick={onClear}
          >
            <DeleteSweepIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
}
